import React from 'react';
import { Link } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { SheetClose } from './ui/sheet';
import FreeShippingBar from './FreeShippingBar';

const FREE_SHIPPING_THRESHOLD = 250;

const CartDrawer = () => {
  const { user } = useAuth();
  const { cart, loading, updateCartItem, removeFromCart } = useCart();

  // Calculate shipping
  const subtotal = cart.total;
  const qualifiesForFreeShipping = subtotal >= FREE_SHIPPING_THRESHOLD;
  const standardShipping = qualifiesForFreeShipping ? 0 : 
    subtotal < 50 ? 7.99 : 
    subtotal < 100 ? 9.99 : 12.99;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-12" data-testid="empty-cart">
        <ShoppingBag className="w-16 h-16 text-[#1E2532] mb-4" />
        <p className="text-[#A1A7B5] mb-4">Your cart is empty</p>
        <SheetClose asChild>
          <Link to="/products">
            <Button className="btn-secondary" data-testid="cart-browse-btn">
              Browse Products
            </Button>
          </Link>
        </SheetClose>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="cart-drawer">
      {/* Free Shipping Progress Bar */}
      <FreeShippingBar subtotal={subtotal} threshold={FREE_SHIPPING_THRESHOLD} />
      
      <div className="flex-1 overflow-y-auto py-4 space-y-4">
        {cart.items.map((item) => (
          <div key={item.product_id} className="flex space-x-4 p-3 glass-card" data-testid={`cart-item-${item.product_id}`}>
            <img
              src={item.image_url || '/placeholder.png'}
              alt={item.name}
              className="w-20 h-20 object-cover rounded-lg"
            />
            <div className="flex-1 min-w-0">
              <h4 className="text-white font-medium truncate">{item.name}</h4>
              <p className="text-[#00F0FF] font-semibold">${item.price.toFixed(2)}</p>
              
              <div className="flex items-center space-x-2 mt-2">
                <button
                  onClick={() => updateCartItem(item.product_id, item.quantity - 1)}
                  className="p-1 bg-[#1E2532] rounded hover:bg-[#00F0FF]/20 transition-colors"
                  data-testid={`cart-decrease-${item.product_id}`}
                >
                  <Minus className="w-4 h-4 text-white" />
                </button>
                <span className="text-white font-medium w-8 text-center">{item.quantity}</span>
                <button
                  onClick={() => updateCartItem(item.product_id, item.quantity + 1)}
                  className="p-1 bg-[#1E2532] rounded hover:bg-[#00F0FF]/20 transition-colors"
                  data-testid={`cart-increase-${item.product_id}`}
                >
                  <Plus className="w-4 h-4 text-white" />
                </button>
                <button
                  onClick={() => removeFromCart(item.product_id)}
                  className="p-1 ml-auto text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded transition-colors"
                  data-testid={`cart-remove-${item.product_id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-[#1E2532] pt-4 space-y-3">
        <div className="flex justify-between items-center text-sm">
          <span className="text-[#A1A7B5]">Subtotal</span>
          <span className="text-white">${subtotal.toFixed(2)}</span>
        </div>
        <div className="flex justify-between items-center text-sm">
          <span className="text-[#A1A7B5]">Shipping (Standard)</span>
          <span className={qualifiesForFreeShipping ? 'text-green-400' : 'text-white'}>
            {qualifiesForFreeShipping ? 'FREE' : `$${standardShipping.toFixed(2)}`}
          </span>
        </div>
        <div className="flex justify-between items-center pt-2 border-t border-[#1E2532]">
          <span className="text-white font-medium">Estimated Total</span>
          <span className="text-white font-semibold text-lg" data-testid="cart-subtotal">
            ${(subtotal + standardShipping).toFixed(2)}
          </span>
        </div>
        <SheetClose asChild>
          <Link to="/checkout" className="block">
            <Button className="btn-primary w-full" data-testid="cart-checkout-btn">
              Proceed to Checkout
            </Button>
          </Link>
        </SheetClose>
      </div>
    </div>
  );
};

export default CartDrawer;
